import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Modal from 'tailwind-components/Modal/Modal';
import { useDispatch } from 'react-redux';
import { showAlert } from 'state/actions/site';
import styles from './releaseRequestSignModal.module.scss';

/**
 * Props:
 *  - active      {boolean}   controla si el modal está visible
 *  - signUrl     {string}    URL del embedtoken de Zoho Sign
 *  - onSigned    {function}  callback cuando Zoho confirma la firma (refresca + cierra)
 */
function ReleaseRequestSignModal({ active, signUrl, onSigned }) {
  const dispatch = useDispatch();
  const hasHandledSign = useRef(false);

  // Zoho Sign emite un postMessage cuando el agente termina de firmar
  useEffect(() => {
    if (!active) return;

    const handleMessage = e => {
      const raw =
        typeof e.data === 'string' ? e.data : JSON.stringify(e.data ?? '');

      const isCompleted =
        raw.includes('"action":"sign"') ||
        raw.includes('"status":"success"') ||
        raw.toLowerCase().includes('finished');

      if (isCompleted && !hasHandledSign.current) {
        hasHandledSign.current = true;
        dispatch(
          showAlert({
            message: 'Release request signed successfully.',
            code: 'success'
          })
        );
        onSigned();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [active, dispatch, onSigned]);

  // Resetear la ref cuando se abre el modal de nuevo
  useEffect(() => {
    if (active) {
      hasHandledSign.current = false;
    }
  }, [active]);

  return (
    <Modal
      active={active}
      closeModal={onSigned}
      title='Agent Release Request — Signature'
      size='xlarger'
    >
      <div className={styles.iframeWrapper}>
        {signUrl ? (
          <iframe
            src={signUrl}
            title='Zoho Sign — Release Request'
            className={styles.signIframe}
            allow='camera'
            sandbox='allow-scripts allow-same-origin allow-forms allow-popups allow-top-navigation allow-modals'
          />
        ) : (
          <p className={styles.loadingText}>Loading signature document...</p>
        )}
      </div>
    </Modal>
  );
}

ReleaseRequestSignModal.propTypes = {
  active: PropTypes.bool.isRequired,
  signUrl: PropTypes.string,
  onSigned: PropTypes.func.isRequired
};

ReleaseRequestSignModal.defaultProps = {
  signUrl: null
};

export default ReleaseRequestSignModal;
